import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { usePortfolio } from "@/context/PortfolioContext";
import Reveal from "@/components/site/Reveal";
import { RevealTextInstant } from "@/components/site/RevealText";
import { PROJECT_CATEGORIES } from "@/lib/data";
import { AnimatePresence, motion } from "framer-motion";

const EASE = [0.76, 0, 0.24, 1];

// Asymmetric layout classes for bento grid
const LAYOUTS = [
  "col-span-12 md:col-span-8 md:row-span-2",
  "col-span-12 md:col-span-4",
  "col-span-12 md:col-span-4",
  "col-span-12 md:col-span-6",
  "col-span-12 md:col-span-6",
  "col-span-12 md:col-span-12",
];
const ASPECTS = [
  "aspect-[16/11] md:aspect-[16/13]",
  "aspect-[4/3]",
  "aspect-[4/3]",
  "aspect-[16/10]",
  "aspect-[16/10]",
  "aspect-[21/9]",
];

function itemTitle(it, lang) { return lang === "en" ? (it.title_en || it.title_it) : (it.title_it || it.title_en); }
function itemField(it, key, lang) {
  const en = it[`${key}_en`];
  const itVal = it[`${key}_it`];
  return lang === "en" ? (en || itVal) : (itVal || en);
}

export default function PortfolioPage() {
  const { t, lang } = useLang();
  const { visible, items, loading, imageUrl } = usePortfolio();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  // If portfolio was hidden via admin, redirect home.
  useEffect(() => {
    if (!loading && !visible) navigate("/", { replace: true });
  }, [loading, visible, navigate]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((p) => p.category === filter);
  }, [filter, items]);

  return (
    <div data-testid="page-portfolio" className="pt-32 md:pt-40">
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pb-16">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-3 az-tag"><span className="az-tag__dot" /><span>{t.portfolio.kicker}</span></div>
          <div className="col-span-12 md:col-span-3 md:col-start-10 md:text-right font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
            {String(filtered.length).padStart(2, "0")} / archive
          </div>
        </div>
        <RevealTextInstant
          as="h1"
          lines={[t.portfolio.title]}
          className="font-serif tracking-tighter leading-[0.94] text-[13vw] md:text-[9vw] lg:text-[8rem]"
          lineClassName="block"
        />
        <div className="mt-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <Reveal delay={0.15}>
              <p className="font-sans text-lg md:text-xl leading-relaxed text-[#86868B] max-w-[52ch]">
                {t.portfolio.lead}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-6 md:py-10 border-t border-b border-[#E5E5EA]">
        <div className="flex flex-wrap gap-2 md:gap-3" role="tablist" data-testid="portfolio-filters">
          {PROJECT_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c}`}
              className={[
                "px-4 py-2 rounded-full font-mono text-[11px] tracking-widest uppercase border transition-colors duration-400",
                filter === c
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-[#0A0A0A] border-[#E5E5EA] hover:border-[#0A0A0A]",
              ].join(" ")}
            >
              {t.portfolio.categories[c] || c}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-24">
        {filtered.length === 0 && !loading && (
          <div className="font-mono text-[11px] tracking-widest uppercase text-[#86868B] text-center py-24" data-testid="portfolio-empty">
            Nessun progetto in questa categoria.
          </div>
        )}

        <motion.div layout className="grid grid-cols-12 auto-rows-min gap-4 md:gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => {
              const title = itemTitle(p, lang);
              const layout = LAYOUTS[i % LAYOUTS.length];
              const aspect = ASPECTS[i % ASPECTS.length];
              return (
                <motion.button
                  layout
                  key={p.id}
                  onClick={() => setActive(p)}
                  data-testid={`portfolio-card-${p.id}`}
                  data-cursor-label={t.common.viewProject}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.04 }}
                  className={`${layout} block text-left az-card`}
                >
                  <div className={`relative ${aspect}`}>
                    {p.image_file_id ? (
                      <img
                        src={imageUrl(p.id)}
                        alt={title}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A] text-white/40 font-mono text-[10px] tracking-widest uppercase">
                        No image
                      </div>
                    )}
                    <div className="absolute top-4 left-4 right-4 flex justify-between text-white font-mono text-[10px] tracking-widest uppercase">
                      <span>{(t.portfolio.categories[p.category] || p.category)}</span>
                      <span>{p.year || ""}</span>
                    </div>
                    <div className="az-card__meta">
                      <div className="font-serif text-2xl md:text-4xl tracking-tight max-w-[70%]">{title}</div>
                      <div className="font-mono text-[10px] tracking-widest uppercase text-white/70">{p.coord || ""}</div>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="portfolio-modal"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="absolute inset-x-0 bottom-0 top-16 md:top-24 bg-white overflow-y-auto"
            >
              <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-10 md:py-16">
                <div className="flex items-start justify-between mb-8">
                  <div className="az-tag"><span className="az-tag__dot" /><span>{(t.portfolio.categories[active.category] || active.category)}</span></div>
                  <button onClick={() => setActive(null)} className="font-mono text-[11px] tracking-widest uppercase" data-testid="close-portfolio-modal">Close ×</button>
                </div>
                <div className="grid grid-cols-12 gap-8">
                  <div className="col-span-12 md:col-span-7">
                    <div className="relative aspect-[16/11] overflow-hidden bg-[#0A0A0A]">
                      {active.image_file_id && (
                        <img src={imageUrl(active.id)} alt={itemTitle(active, lang)} className="absolute inset-0 w-full h-full object-cover" />
                      )}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-5 space-y-8">
                    <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.98]">{itemTitle(active, lang)}</h2>
                    {itemField(active, "challenge", lang) && (
                      <div>
                        <div className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF] mb-2">{t.portfolio.labels.challenge}</div>
                        <p className="font-sans text-base md:text-lg leading-relaxed">{itemField(active, "challenge", lang)}</p>
                      </div>
                    )}
                    {itemField(active, "approach", lang) && (
                      <div>
                        <div className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF] mb-2">{t.portfolio.labels.approach}</div>
                        <p className="font-sans text-base md:text-lg leading-relaxed">{itemField(active, "approach", lang)}</p>
                      </div>
                    )}
                    {itemField(active, "result", lang) && (
                      <div>
                        <div className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF] mb-2">{t.portfolio.labels.result}</div>
                        <p className="font-sans text-base md:text-lg leading-relaxed">{itemField(active, "result", lang)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
